import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ButtonBase,
  Stack,
  styled,
  useTheme,
} from '@mui/material'
import Typography from '../../../../components/Typography'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import React, { SetStateAction, useEffect, useMemo, useState } from 'react'
import Input from '../../../../components/Input'
import { useCampaignContext } from './CampaignContext'
import { formatPrice } from '../../../../util/helpers'
import { SaleAgreementPromo, SaleAgreementPromoProduct } from './types'
import _ from 'lodash'
import { ModalActions } from '../../../../components/Modal'
import { Button } from '../../../../components/Button'
import TestIds from '../../../../testIds'
import { calculateTotalFreightCost, getPrice, unitMapPlural } from './utils'
import dayjs from 'dayjs'
import { colors } from '../../../../themes/colors'
import TabHeader from './TabHeader'
import { EditButton } from '../../../products/components/ProductCard/EditButton'
import { EditSalesAgreementProductPriceModal } from './EditSalesAgreementProductModal'

export const getTotalUnits = (products: SaleAgreementPromoProduct[], id: string): number => {
  const orders = products.find((product) => product.itemNumber === id)?.orders
  return orders?.reduce((sum, order) => sum + order.quantity, 0) ?? 0
}
interface QuantityTabProps {
  config: SaleAgreementPromo
}

export const QuantityTab: React.FC<QuantityTabProps> = ({ config }) => {
  const { state, setState } = useCampaignContext()
  const [error, setError] = useState<
    Record<string, { minMonth: boolean; totalMin: boolean; monthMinOrder?: boolean[] }>
  >({})
  const [touched, setTouched] = useState(false)
  const isFreightInclusive = !config.freightRequired

  useEffect(() => {
    if (!state.productSelected) return

    const newErrorState: Record<
      string,
      { minMonth: boolean; totalMin: boolean; monthMinOrder?: boolean[] }
    > = state.productSelected.reduce(
      (
        acc: Record<string, { minMonth: boolean; totalMin: boolean; monthMinOrder?: boolean[] }>,
        product,
      ) => {
        const totalOrders = product.orders?.reduce((sum, order) => sum + order.quantity, 0) ?? 0
        const totalMinError = totalOrders < product.quantity

        const length = product?.orders?.filter((order) => order.quantity > 0).length ?? 0

        const monthMinOrderError = product.orders?.map((order) => {
          return (
            order.quantity !== 0 &&
            (order.quantity < (product.minReleaseQuantity ?? 0) ||
              order.quantity > (product.maxReleaseQuantity ?? 999))
          )
        })

        const minMonthError = length < (config.minPeriods ?? 0)

        acc[product.itemNumber] = {
          minMonth: minMonthError,
          totalMin: totalMinError,
          monthMinOrder: monthMinOrderError,
        }
        return acc
      },
      {} as Record<string, { minMonth: boolean; totalMin: boolean; monthMinOrder: boolean[] }>,
    )
    setError(newErrorState)
  }, [state.productSelected, config.minPeriods])

  const [editPriceOpen, setEditPriceOpen] = React.useState(false)
  const [editProduct, setEditProduct] = React.useState<SaleAgreementPromoProduct>()

  const handleOpenEditPriceModal = (product: SaleAgreementPromoProduct) => {
    setEditProduct(product)
    setEditPriceOpen(true)
  }

  /**
   * Applies the edited price and quantity to a specific SaleAgreementPromoProduct.
   *
   * @param newPrice - The new unit price to apply.
   * @param newQuantity - The new quantity to apply.
   * @param item - The SaleAgreementPromoProduct item to update.
   */
  const onApplyEditedPrice = (newPrice: number, item: SaleAgreementPromoProduct) => {
    const updatedProducts = state.productSelected.map((product) => {
      if (product.itemNumber === item.itemNumber) {
        return {
          ...product,
          editedPrice: newPrice, // Set editedPrice
        }
      }
      return product
    })

    const newState = {
      productSelected: updatedProducts,
    }

    setState(newState)
  }

  /**
   * Reverts the edited price of a specific SaleAgreementPromoProduct.
   *
   * @param item - The SaleAgreementPromoProduct item to revert.
   */
  const onRevertEditedPrice = (item: SaleAgreementPromoProduct) => {
    const updatedProducts = state.productSelected.map((product) => {
      if (product.itemNumber === item.itemNumber) {
        return {
          ...product,
          editedPrice: undefined, // Remove editedPrice
        }
      }
      return product
    })

    const revertedPrice = {
      productSelected: updatedProducts,
    }

    setState(revertedPrice)
  }

  return (
    <Stack direction='column' gap={'1rem'} pb='100px'>
      <EditSalesAgreementProductPriceModal
        open={editPriceOpen}
        setOpen={setEditPriceOpen}
        onApplyEditedPrice={onApplyEditedPrice}
        onRevertEditedPrice={onRevertEditedPrice}
        item={editProduct}
      />
      <TabHeader
        title='Delivery plan'
        caption='Enter quantity required each month for every product selected'
      />
      {state.productSelected.map((prod) => {
        const handleEditPriceCallback = config.editPriceAllowed
          ? () => {
              handleOpenEditPriceModal(prod)
            }
          : undefined

        const summary: AccordionItemSummary[] = [
          {
            type: 'unit',
            title: prod.unit,
            calculation: prod.orders ? prod.orders.reduce((a, b) => a + b.quantity, 0) : 0,
          },
          {
            type: 'price',
            title: 'Total',
            calculation: getPrice(prod) * getTotalUnits(state.productSelected, prod.itemNumber),
          },
        ]

        if (!isFreightInclusive) {
          summary.push({
            type: 'price',
            title: 'Freight',
            calculation: calculateTotalFreightCost(prod, state.freightCharge),
          })
        }

        return (
          <SalesAccordionItem
            config={config}
            allowRemoveProduct
            error={error[prod.itemNumber]}
            key={prod.itemNumber}
            productId={prod.itemNumber}
            unit={prod.unit}
            minQuantity={prod.quantity}
            touched={touched}
            setTouched={setTouched}
            title={prod.description}
            defaultExpanded
            handleOpenEditPriceModal={handleEditPriceCallback}
            summary={summary}
          />
        )
      })}
      <ModalActions showDivider>
        <Button
          variant='contained'
          // disabled
          onClick={() => {
            if (!error) return
            const value = Object.values(error)

            const minMonthError = value.map((v) => v.minMonth)
            const totalMinError = value.map((v) => v.totalMin)
            const monthMinOrderError = value.flatMap((v) => v.monthMinOrder?.map((value) => value))

            if (
              !minMonthError.includes(true) &&
              !totalMinError.includes(true) &&
              !monthMinOrderError.includes(true)
            ) {
              setState({ tabIndex: 3 })
            }
            setTouched(true)
          }}
          // data-testid={TestIds.feat.product.details.orderButton}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              width: '100%',
            },
          })}
        >
          Next Step
        </Button>
      </ModalActions>
    </Stack>
  )
}

export interface AccordionItemSummary {
  title: string
  type: 'price' | 'unit'
  calculation?: number
}

interface SalesAccordionItemProps {
  title: string
  config?: SaleAgreementPromo
  summary?: AccordionItemSummary[]
  minQuantity: number
  error?: { minMonth: boolean; totalMin: boolean; monthMinOrder?: boolean[] } // Simplified to boolean per product
  touched?: boolean
  unit: string
  setTouched?: React.Dispatch<SetStateAction<boolean>>
  productId: string
  disabled?: boolean // disabled accordian toggle
  allowRemoveProduct?: boolean
  editable?: boolean // disabled input edit
  defaultExpanded?: boolean // default value of expand
  handleOpenEditPriceModal?: () => void
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  background: 'white',
  border: `1px solid ${theme.colors.grey400}`,
  borderRadius: '0 !important',
  '&.Mui-expanded': {
    margin: '0 !important',
  },
}))

const StyledAccordionSummary = styled(AccordionSummary)({
  padding: '1rem',
  '& .MuiAccordionSummary-content': {
    margin: '0 !important',
  },
})

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  borderTop: `1px solid ${theme.colors.grey400}`,
  paddingX: '1rem',
  paddingTop: '1rem',
  paddingBottom: '1.5rem',
  backgroundColor: theme.colors.primary50,
}))

const StyledExpandButton = styled(ButtonBase)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
})

const DivGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(7, minmax(0,1fr))',
  },

  columnGap: '2rem',
  rowGap: '1rem',
}))

export const SalesAccordionItem: React.FC<SalesAccordionItemProps> = ({
  title,
  config,
  summary,
  minQuantity,
  productId,
  unit,
  disabled,
  error,
  setTouched,
  touched,
  allowRemoveProduct,
  editable = true,
  defaultExpanded = false,
  handleOpenEditPriceModal,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const theme = useTheme()
  const { setState, state } = useCampaignContext()
  const currentProduct = state.productSelected.find((a) => a.itemNumber === productId)
  const otherProduct = state.productSelected.filter((a) => a.itemNumber !== productId)

  const renderSummaryBlocks = useMemo(() => {
    if (!summary) return
    if (summary)
      return summary.map((item) => {
        if (item.type === 'unit') {
          const totalUnit = getTotalUnits(state.productSelected, productId)
          return (
            <Stack direction='column' gap='4px' key={`${item.title}-${item.type}`}>
              <Input
                onFocus={(e) => e.currentTarget.select()}
                sx={{
                  WebkitTextFillColor: 'black',
                  color: 'black',
                  backgroundColor: theme.colors.grey100,

                  minWidth: '84px',
                  maxWidth: '84px',
                  minHeight: '45px',
                  maxHeight: '45px',
                }}
                disabled
                label={unitMapPlural[unit as keyof typeof unitMapPlural]}
                testId={TestIds.feat.agreement.magnesium.quantityTab.totalUnit}
                disableError
                value={totalUnit === 0 ? 0 : _.trimStart(String(totalUnit), '0')}
                type='number'
              />
            </Stack>
          )
        }

        if (item.type === 'price') {
          return (
            <Stack direction='column' gap='4px' key={`${item.title}-${item.type}`}>
              <Typography variant='body2' sx={{ textTransform: 'capitalize' }} fontWeight={700}>
                {item.title}
              </Typography>
              <Stack direction='column'>
                <Typography
                  lineHeight={'27px'}
                  variant='body1'
                  color='secondary700'
                  fontWeight={700}
                >
                  {formatPrice(item.calculation ?? 0)}
                </Typography>
                <Typography fontSize='12px' lineHeight={'18px'} color='secondary700'>
                  GST excl.
                </Typography>
              </Stack>

              {handleOpenEditPriceModal && item.title === 'Total' && (
                <EditButton onClick={handleOpenEditPriceModal} />
              )}
            </Stack>
          )
        }
      })
  }, [summary, setState])

  const toggleExpand = () => {
    if (!disabled) {
      setExpanded((prev) => !prev)
    }
  }

  const expandButtonLabel = () => {
    if (editable) {
      return 'Expand to edit details'
    }
    if (expanded) {
      return 'Collapse'
    }
    return 'Expand'
  }

  const renderActions = () => {
    const sx = {
      padding: '0rem 0',
    }

    if (allowRemoveProduct) {
      return (
        <Button
          disableRipple
          onClick={() => {
            if (otherProduct.length === 0) {
              setState({
                tabIndex: 1,
                productSelected: [...otherProduct],
              })
            } else
              setState({
                productSelected: [...otherProduct],
              })
          }}
          variant='text'
          sx={sx}
        >
          <Typography variant='caption'>Remove</Typography>
        </Button>
      )
    }

    if (!editable) {
      return (
        <Button
          disableRipple
          onClick={() => {
            setState({
              tabIndex: 2,
            })
          }}
          variant='text'
          sx={sx}
        >
          <Typography variant='caption'>Edit</Typography>
        </Button>
      )
    }
  }

  return (
    <StyledAccordion expanded={expanded}>
      <StyledAccordionSummary>
        <Stack direction='column' gap='1rem' width='100%'>
          <Stack
            direction='row'
            width='100%'
            justifyContent={'space-between'}
            gap={2}
            alignItems='start'
          >
            <Typography variant='h4'>{title}</Typography>
            {renderActions()}
          </Stack>
          <Stack direction='column'>
            <Stack direction='row' alignItems='start' gap='1rem'>
              {renderSummaryBlocks}
            </Stack>
            {error?.totalMin && touched ? (
              <Typography variant='body2' color='error700' fontStyle={'italic'} alignSelf={'end'}>
                Minimum {minQuantity} total {unit}
              </Typography>
            ) : null}

            {error?.minMonth && touched ? (
              <Typography variant='body2' color='error700' fontStyle={'italic'} alignSelf={'end'}>
                Minimum {config?.minPeriods ?? 0} months to be fulfilled
              </Typography>
            ) : null}
            {error?.monthMinOrder?.includes(true) && touched ? (
              <Typography variant='body2' color='error700' fontStyle={'italic'} alignSelf={'end'}>
                Minimum {currentProduct?.minReleaseQuantity ?? 0}, Maximum{' '}
                {currentProduct?.maxReleaseQuantity ?? 999} per populated month
              </Typography>
            ) : null}
          </Stack>

          <StyledExpandButton disableRipple onClick={toggleExpand}>
            <Typography variant='caption'>{expandButtonLabel()}</Typography>
            <MaterialSymbol
              sx={{
                transitionDuration: '300ms',
                fontSize: '1.25rem',
                transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
              }}
            >
              keyboard_arrow_up
            </MaterialSymbol>
          </StyledExpandButton>
        </Stack>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        {/* TODO: When requirements are confirmed */}
        {/* <StyledPlannedDeliveryContainer>
          <Typography variant='body2' fontWeight={700}>
            Planned delivery months
          </Typography>
          <StyledSelect
            IconComponent={() => null}
            endAdornment={
              <MaterialSymbol color={theme.colors.primary500}>keyboard_arrow_down</MaterialSymbol>
            }
          >
            <MenuItem value={3}>3 months - $649.00 per sling</MenuItem>
            <MenuItem value={6}>6 months - $599.00 per sling</MenuItem>
            <MenuItem value={9}>9 months - $549.00 per sling</MenuItem>
          </StyledSelect>
        </StyledPlannedDeliveryContainer> */}
        <DivGrid>
          {currentProduct?.orders?.map((order, index) => {
            let label = `${dayjs().month(order.month).year(order.year).format('MMM')}`
            if (index === 0) {
              label = `${dayjs().month(order.month).year(order.year).format('MMM-YYYY')}`
            } else if (
              currentProduct.orders &&
              order.year > currentProduct.orders[0].year &&
              order.year > currentProduct.orders[index - 1].year
            ) {
              label = `${dayjs().month(order.month).year(order.year).format('MMM-YYYYY')}`
            }
            return (
              <Input
                disabled={!editable}
                // error={order.quantity < minQuantity}
                disableError
                onPaste={(e) => {
                  const pasteData = e.clipboardData.getData('Text')
                  // Allow only digits
                  if (!/^\d*$/.test(pasteData)) {
                    e.preventDefault()
                  }
                }}
                onFocus={(e) => e.currentTarget.select()}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === '+' || e.key === '-' || e.key === '.')
                    e.preventDefault()
                }}
                onChange={(e) => {
                  setTouched?.(true)

                  const value = Math.max(0, Number(e.target.value))
                  const updatedOrders = currentProduct.orders?.map((o) =>
                    o.year === order.year && o.month === order.month
                      ? { ...o, quantity: value }
                      : o,
                  )

                  if (!updatedOrders) return

                  const updatedProduct = {
                    ...currentProduct,
                    orders: updatedOrders,
                  }

                  setState({
                    productSelected: [...otherProduct, updatedProduct],
                  })
                }}
                error={error?.monthMinOrder?.[index]}
                label={label}
                testId=''
                type='number'
                key={`${order.month}/${order.year}`}
                value={order.quantity === 0 ? 0 : _.trimStart(String(order.quantity), '0')}
                sx={{
                  '&.Mui-disabled': {
                    backgroundColor: colors.grey100,
                  },
                }}
              />
            )
          })}
        </DivGrid>
      </StyledAccordionDetails>
    </StyledAccordion>
  )
}
